import { Component } from '@theme/component';

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * About page counters. When the row scrolls into view each number counts up from 0 to its value once. A value is
 * split into a prefix, a number and a suffix ("50,000+" becomes "", 50000, "+"), so text such as "Pan India" is
 * left as written. The final value is always in the markup, so without JavaScript or with reduced motion the
 * numbers simply show as they are.
 *
 * @typedef {object} CounterRefs
 * @property {HTMLElement[]} [values]
 *
 * @extends {Component<CounterRefs>}
 */
class TphAboutCounterComponent extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {Set<number>} */
  #frames = new Set();
  #done = false;

  connectedCallback() {
    super.connectedCallback();
    if (this.dataset.count !== 'true' || reducedMotion.matches || this.#done) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#observer?.disconnect();
        this.#run();
      },
      { threshold: 0.35 }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#frames.forEach((frame) => cancelAnimationFrame(frame));
    this.#frames.clear();
  }

  #run() {
    this.#done = true;
    const duration = Math.max(500, Number(this.dataset.duration) || 2000);

    for (const element of this.refs.values ?? []) {
      const final = element.dataset.final ?? element.textContent ?? '';
      const match = final.match(/^(\D*?)(\d[\d,]*(?:\.\d+)?)(.*)$/);
      if (!match) continue;

      const [, prefix = '', digits = '', suffix = ''] = match;
      const target = Number(digits.replace(/,/g, ''));
      if (!Number.isFinite(target)) continue;

      const decimals = digits.includes('.') ? (digits.split('.')[1]?.length ?? 0) : 0;
      const grouped = digits.includes(',');

      const format = (/** @type {number} */ value) =>
        `${prefix}${value.toLocaleString('en-IN', {
          useGrouping: grouped,
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        })}${suffix}`;

      element.textContent = format(0);
      const start = performance.now();
      const tick = (/** @type {number} */ now) => {
        const progress = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        element.textContent = progress < 1 ? format(target * eased) : final;
        if (progress < 1) this.#frames.add(requestAnimationFrame(tick));
      };
      this.#frames.add(requestAnimationFrame(tick));
    }
  }
}

if (!customElements.get('tph-about-counter-component')) {
  customElements.define('tph-about-counter-component', TphAboutCounterComponent);
}
